<script setup>
import {ref} from 'vue';

/**
 * JoinRequestPending — shows that the user has a pending join request for an
 * entity, with a confirm-gated cancel. Two looks:
 *  - banner  (default): a full-width card (club / league-cycle pages)
 *  - compact: an action-strip pill matching the global `.as` primitive
 *             (ladder / tournament action strips)
 * Purely presentational; the page owns the status via useJoinRequestStatus and
 * handles @cancel.
 */
defineProps({
  entityName: {type: String, default: ''},
  cancelling: {type: Boolean, default: false},
  compact: {type: Boolean, default: false},
});

const emit = defineEmits(['cancel']);
const dialog = ref(false);
const confirm = () => emit('cancel');
</script>

<template>
  <!-- compact: action-strip pill -->
  <button v-if="compact" type="button" class="as jrp-pill" @click="dialog = true">
    <v-icon size="17" icon="mdi-clock-outline"/>Requested
  </button>

  <!-- banner: full card -->
  <div v-else class="tl-card jrp-banner">
    <div class="jrp-ic"><v-icon color="warning">mdi-clock-outline</v-icon></div>
    <div class="flex-fill">
      <div class="jrp-title">Your request to join is pending</div>
      <div class="jrp-sub">An admin will review it shortly and we'll let you know.</div>
    </div>
    <v-btn variant="outlined" color="error" rounded="pill" prepend-icon="mdi-close" @click="dialog = true">
      Cancel request
    </v-btn>
  </div>

  <!-- shared confirm -->
  <v-dialog v-model="dialog" max-width="420">
    <div class="tl-confirm">
      <h3>Cancel your request?</h3>
      <p>
        Your request to join <strong v-if="entityName">{{ entityName }}</strong><span v-else>this</span>
        will be withdrawn. You can request to join again later.
      </p>
      <div class="tl-confirm-actions">
        <v-btn variant="text" size="large" @click="dialog = false">Keep request</v-btn>
        <v-btn color="error" variant="flat" size="large" :loading="cancelling" @click="confirm">
          Cancel request
        </v-btn>
      </div>
    </div>
  </v-dialog>
</template>

<style scoped lang="scss">
/* compact pill — warning-toned variant of the global .as primitive */
.jrp-pill {
  color: rgb(var(--v-theme-warning));

  :deep(.v-icon) {
    color: rgb(var(--v-theme-warning));
  }
}

/* banner card */
.jrp-banner {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  margin-bottom: 20px;
}

.jrp-ic {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--v-theme-warningTint));
}

.jrp-title {
  font-size: 14px;
  font-weight: 700;
}

.jrp-sub {
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 2px;
}
</style>
